import React, { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import type { RootState } from "@redux/store/store";
import {
  setCategoryFilterSelection,
  setProductFilterSelection,
  setTagFilterSelection,
  TAGS_MANAGER_NO_CATEGORY_SENTINEL,
} from "@redux/slices/tagsManagerFiltersSlice";
import MultiFilterDropdown, {
  type MultiFilterFieldConfig,
  type MultiFilterValue,
} from "@ui-components/Filter/MultiFilterDropdown";
import { listSupportedProducts } from "./requests/productAdapters";
import { useTagRows } from "./hooks";

const TAG_FIELD_ID = "tag";
const CATEGORY_FIELD_ID = "category";
const PRODUCT_FIELD_ID = "product";

/**
 * Self-contained Tags Manager global filter. Reads source rows from
 * the store via {@link useTagRows} so it can be mounted anywhere — in
 * the SideNav top bar slot (current placement, mirroring Backbone) or
 * inline on a page — without prop wiring.
 *
 * Adapter that maps the Tags Manager filter slice to the generic
 * {@link MultiFilterDropdown}. Mirrors `BackboneFilters` (see the
 * `global-multifilter` skill for the canonical pattern).
 */
const TagsManagerFilters: React.FC = () => {
  const { rows } = useTagRows();
  const dispatch = useAppDispatch();
  const {
    tagFilterSelection,
    categoryFilterSelection,
    productFilterSelection,
  } = useAppSelector((state: RootState) => state.tagsManagerFilters);

  const fields: MultiFilterFieldConfig[] = useMemo(() => {
    const out: MultiFilterFieldConfig[] = [];

    // --- Tag (by id, label = name)
    if (rows.length > 0) {
      const sortedTags = [...rows].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      out.push({
        id: TAG_FIELD_ID,
        label: "Tag",
        options: sortedTags.map((t) => ({
          id: t.id,
          label: t.name,
          count: t.resourceCount,
        })),
        searchable: true,
        searchPlaceholder: "Search tags…",
        emptyOptionsMessage: "No tags",
      });
    }

    // --- Category (with sentinel for "no category" rows)
    const categoryCounts = new Map<string, number>();
    let noCategoryCount = 0;
    for (const r of rows) {
      if (r.category) {
        const name = r.category.name;
        categoryCounts.set(name, (categoryCounts.get(name) ?? 0) + 1);
      } else {
        noCategoryCount += 1;
      }
    }
    const sortedCategories = Array.from(categoryCounts.entries()).sort(
      ([a], [b]) => a.localeCompare(b)
    );
    if (sortedCategories.length > 0 || noCategoryCount > 0) {
      out.push({
        id: CATEGORY_FIELD_ID,
        label: "Category",
        options: [
          ...sortedCategories.map(([cat, count]) => ({
            id: cat,
            label: cat,
            count,
          })),
          ...(noCategoryCount > 0
            ? [
                {
                  id: TAGS_MANAGER_NO_CATEGORY_SENTINEL,
                  label: "(No category)",
                  count: noCategoryCount,
                },
              ]
            : []),
        ],
        searchable: false,
        emptyOptionsMessage: "No categories",
      });
    }

    // --- Product (where the tag's resources live)
    const productCounts = new Map<string, number>();
    for (const r of rows) {
      for (const p of r.productSpread) {
        productCounts.set(p, (productCounts.get(p) ?? 0) + 1);
      }
    }
    const products = listSupportedProducts();
    const productOptions = products
      .filter((p) => productCounts.has(p.productLabel))
      .map((p) => ({
        id: p.productLabel,
        label: p.productLabel,
        count: productCounts.get(p.productLabel) ?? 0,
      }));
    if (productOptions.length > 0) {
      out.push({
        id: PRODUCT_FIELD_ID,
        label: "Service",
        options: productOptions,
        searchable: false,
        emptyOptionsMessage: "No services in use",
      });
    }

    return out;
  }, [rows]);

  const value: MultiFilterValue = useMemo(
    () => ({
      [TAG_FIELD_ID]: tagFilterSelection,
      [CATEGORY_FIELD_ID]: categoryFilterSelection,
      [PRODUCT_FIELD_ID]: productFilterSelection,
    }),
    [tagFilterSelection, categoryFilterSelection, productFilterSelection]
  );

  const handleApply = (next: MultiFilterValue) => {
    const nextTag = next[TAG_FIELD_ID] ?? [];
    const nextCategory = next[CATEGORY_FIELD_ID] ?? [];
    const nextProduct = next[PRODUCT_FIELD_ID] ?? [];
    dispatch(setTagFilterSelection(nextTag));
    dispatch(setCategoryFilterSelection(nextCategory));
    dispatch(setProductFilterSelection(nextProduct));
  };

  if (fields.length === 0) return null;

  return (
    <div className="w-full">
      <MultiFilterDropdown
        fields={fields}
        value={value}
        onApply={handleApply}
        triggerLabel="Filters"
        cardTitle="Filters"
      />
    </div>
  );
};

export default TagsManagerFilters;
